import { ESignProvider, ESignResult } from '@celito.clients/provider';
import { ReactElement } from 'react';

/**
 * WI-F7 — app-side provider model (ADO #38535).
 *
 * The core {@link ESignProvider} contract (in `libs/core`) is popup/redirect
 * shaped: `authenticate()` resolves the token directly. Okta authenticates via
 * an in-modal WIDGET instead, so the Okta provider extends the contract with a
 * `renderWidget` surface. The modal renders that surface when present and calls
 * `authenticate()` otherwise — so a third OIDC provider is a new implementation
 * of one shape or the other, never a modal rewrite.
 */

/** Props the modal hands a widget provider's in-modal render surface. */
export interface ESignWidgetRenderProps {
  /** Called with the extracted {@link ESignResult} once the widget succeeds. */
  onSuccess: (result: ESignResult) => void;
  onError: (err: Error) => void;
  recordName?: string;
  objectName?: string;
}

/** A provider that authenticates through an in-modal widget (e.g. Okta). */
export interface WidgetESignProvider extends ESignProvider {
  renderWidget(props: ESignWidgetRenderProps): ReactElement;
}

/** Either provider shape the registry can return. */
export type AppESignProvider = ESignProvider | WidgetESignProvider;

/** Narrow to a widget provider (has an in-modal render surface). */
export const isWidgetProvider = (
  provider: AppESignProvider | null | undefined
): provider is WidgetESignProvider =>
  !!provider &&
  typeof (provider as WidgetESignProvider).renderWidget === 'function';
